import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getClubByUser,
  getClubByTeacher,
  getClubById,
} from "../../../services/clubServices";
import {
  getDojosByClub,
  getDojosByTeacher,
} from "../../../services/dojoServices";
import {
  getGroupsByDojo,
  getGroupsByTeacher,
} from "../../../services/groupServices";
import attendanceServices from "../../../services/attendanceServices";
import { getAdherentsByGroup } from "../../../services/adherentServices";
import { getTeacherByUser } from "../../../services/teacherServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { isDemoUser } from "./../../../utils/isDemoUser";
import {
  FaCalendarAlt,
  FaChartBar,
  FaCheckCircle,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaMedkit,
  FaPercentage,
  FaTable,
  FaTimesCircle,
  FaTrash,
  FaTrophy,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getDaysInMonth(month) {
  if (!month) return [];
  const [year, monthIndex] = month.split("-").map(Number);
  const daysInMonth = new Date(year, monthIndex, 0).getDate();

  return Array.from(
    { length: daysInMonth },
    (_, i) =>
      `${year}-${String(monthIndex).padStart(2, "0")}-${String(i + 1).padStart(
        2,
        "0"
      )}`
  );
}

function formatMonthLabel(month) {
  if (!month) return "";
  return new Date(`${month}-01`).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

function parsePresenceEntries(entries) {
  const result = {};

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const date = String(entry?.date_presence || "").split("T")[0];
    if (!date) return;

    let presences = {};

    if (typeof entry?.presences === "string") {
      try {
        presences = JSON.parse(entry.presences) || {};
      } catch (error) {
        presences = {};
      }
    } else if (
      entry?.presences &&
      typeof entry.presences === "object" &&
      !Array.isArray(entry.presences)
    ) {
      presences = entry.presences;
    }

    Object.entries(presences).forEach(([adherentId, status]) => {
      if (!result[adherentId]) {
        result[adherentId] = {};
      }
      result[adherentId][date] = status;
    });
  });

  return result;
}

function getGroupDojoId(group) {
  return String(group?.dojo_id ?? group?.dojo?.id ?? "");
}

function getStatusCellClass(status) {
  switch (status) {
    case "Présent":
      return "bg-emerald-500 text-white";
    case "Absent":
      return "bg-red-500 text-white";
    case "Malade/Blessé":
      return "bg-amber-500 text-white";
    default:
      return "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500";
  }
}

function getStatusShortLabel(status) {
  switch (status) {
    case "Présent":
      return "P";
    case "Absent":
      return "A";
    case "Malade/Blessé":
      return "M";
    default:
      return "—";
  }
}

function getInitials(adherent) {
  const prenom = adherent?.prenom?.[0] || "";
  const nom = adherent?.nom?.[0] || "";
  return `${prenom}${nom}`.toUpperCase() || "A";
}

function getShortName(adherent) {
  const prenom = adherent?.prenom || "";
  const nom = adherent?.nom || "";
  if (!prenom && !nom) return "Adhérent";
  return `${prenom} ${nom?.[0] || ""}`.trim();
}

function KpiCard({ icon, label, value, colorClass = "" }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
      <div
        className={cn(
          "mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-base",
          colorClass
        )}
      >
        {icon}
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-2 text-2xl font-black text-slate-900 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
}

function DojoCard({ dojo, active, onClick, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "w-full rounded-[22px] border p-4 text-left shadow-sm transition",
        disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100 opacity-60 dark:border-slate-800 dark:bg-slate-800"
          : active
          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100 dark:border-blue-400 dark:bg-blue-500/10 dark:ring-blue-500/20"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
      )}
    >
      <div className="flex items-center gap-4">
        {dojo?.logo_dojo ? (
          <img
            src={dojo.logo_dojo}
            alt={dojo.nom_dojo}
            className="h-16 w-16 rounded-2xl object-cover"
          />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
            <FaMapMarkerAlt className="text-xl" />
          </div>
        )}

        <div className="min-w-0">
          <h3 className="text-base font-black text-slate-900 dark:text-slate-100">
            {dojo.nom_dojo}
          </h3>
          <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
            {dojo.adresse_dojo || "Adresse non renseignée"}
          </p>
        </div>
      </div>
    </button>
  );
}

function GroupCard({ group, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full rounded-[22px] border p-4 text-left shadow-sm transition",
        active
          ? "border-purple-500 bg-purple-50 ring-2 ring-purple-100 dark:border-purple-400 dark:bg-purple-500/10 dark:ring-purple-500/20"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
      )}
    >
      <p className="text-base font-black text-slate-900 dark:text-slate-100">
        {group.nom_groupe}
      </p>
      <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
        {group.description_groupe || "Aucune description"}
      </p>
    </button>
  );
}

const AffichagePresence = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  let userInfo = null;
  try {
    userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
  } catch (error) {
    userInfo = null;
  }

  const userId = userInfo?.userId;
  const roleId = Number(userInfo?.role_id || 0);
  const demoMode = isDemoUser(userInfo);
  const isTeacher = roleId === 6;

  const [loading, setLoading] = useState(true);
  const [deletingMonth, setDeletingMonth] = useState(false);

  const [club, setClub] = useState(null);
  const [teacher, setTeacher] = useState(null);

  const [dojos, setDojos] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [teacherAccessMap, setTeacherAccessMap] = useState({});

  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  const [adherents, setAdherents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});

  useEffect(() => {
    if (userId) {
      fetchAccessData();
    } else {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!selectedDojo) {
      setSelectedGroup("");
      setAdherents([]);
      setAttendanceData({});
    }
  }, [selectedDojo]);

  useEffect(() => {
    if (!selectedGroup) {
      setAdherents([]);
      setAttendanceData({});
      return;
    }

    fetchGroupMonthlyData(selectedGroup, selectedMonth);
  }, [selectedGroup, selectedMonth]);

  const fetchAccessData = async () => {
    try {
      setLoading(true);

      let clubData = null;
      let dojosData = [];
      let groupsData = [];
      let teacherData = null;
      let accessMap = {};

      if (demoMode) {
        clubData = await getClubById(2);
        dojosData = (await getDojosByClub(2)) || [];
        groupsData = (
          await Promise.all(dojosData.map((dojo) => getGroupsByDojo(dojo.id)))
        ).flat();
      } else if (isTeacher) {
        teacherData = await getTeacherByUser(userId);

        if (teacherData?.id) {
          clubData = await getClubByTeacher(teacherData.id);
          dojosData = (await getDojosByTeacher(teacherData.id)) || [];
          groupsData = (await getGroupsByTeacher(teacherData.id)) || [];

          dojosData.forEach((dojo) => {
            accessMap[dojo.id] = groupsData.filter(
              (group) => String(group.dojo_id) === String(dojo.id)
            );
          });
        }
      } else {
        clubData = await getClubByUser(userId);

        if (clubData?.id) {
          dojosData = (await getDojosByClub(clubData.id)) || [];
          groupsData = (
            await Promise.all(dojosData.map((dojo) => getGroupsByDojo(dojo.id)))
          ).flat();
        }
      }

      setClub(clubData?.id ? clubData : null);
      setTeacher(teacherData || null);
      setDojos(Array.isArray(dojosData) ? dojosData : []);
      setAllGroups(Array.isArray(groupsData) ? groupsData : []);
      setTeacherAccessMap(accessMap);

      if (Array.isArray(dojosData) && dojosData.length === 1) {
        setSelectedDojo(String(dojosData[0].id));
      }
    } catch (error) {
      console.error("Erreur lors du chargement des accès :", error);
      addNotification("Erreur lors du chargement des données.", "error");
      setClub(null);
      setTeacher(null);
      setDojos([]);
      setAllGroups([]);
      setTeacherAccessMap({});
    } finally {
      setLoading(false);
    }
  };

  const loadGroupsByDojo = async (dojoId) => {
    try {
      const data = await getGroupsByDojo(dojoId);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Erreur lors du chargement des groupes :", error);
      return [];
    }
  };

  const fetchGroupMonthlyData = async (groupId, month) => {
    try {
      setLoading(true);

      const [adherentsData, monthlyEntries] = await Promise.all([
        getAdherentsByGroup(groupId),
        attendanceServices.getGroupAttendances(groupId, month),
      ]);

      setAdherents(Array.isArray(adherentsData) ? adherentsData : []);
      setAttendanceData(parsePresenceEntries(monthlyEntries));
    } catch (error) {
      console.error("Erreur lors du chargement du tableau de présence :", error);
      setAdherents([]);
      setAttendanceData({});
      addNotification("Erreur lors du chargement des présences.", "error");
    } finally {
      setLoading(false);
    }
  };

  const days = useMemo(() => getDaysInMonth(selectedMonth), [selectedMonth]);

  const availableGroups = useMemo(() => {
    if (!selectedDojo) return [];

    if (isTeacher) {
      return teacherAccessMap[selectedDojo] || [];
    }

    const existingGroups = allGroups.filter(
      (group) => getGroupDojoId(group) === String(selectedDojo)
    );

    if (existingGroups.length > 0) {
      return existingGroups;
    }

    return [];
  }, [selectedDojo, allGroups, isTeacher, teacherAccessMap]);

  useEffect(() => {
    if (!selectedDojo) return;

    const hasSelectedGroup = availableGroups.some(
      (group) => String(group.id) === String(selectedGroup)
    );

    if (!hasSelectedGroup) {
      setSelectedGroup("");
      setAdherents([]);
      setAttendanceData({});
    }

    if (availableGroups.length === 1 && !selectedGroup) {
      setSelectedGroup(String(availableGroups[0].id));
    }
  }, [selectedDojo, availableGroups, selectedGroup]);

  const selectedGroupObject = useMemo(() => {
    return availableGroups.find(
      (group) => String(group.id) === String(selectedGroup)
    );
  }, [availableGroups, selectedGroup]);

  const dailyStats = useMemo(() => {
    return days.map((day) => {
      let present = 0;
      let absent = 0;
      let sick = 0;

      adherents.forEach((adherent) => {
        const status = attendanceData[String(adherent.id)]?.[day];
        if (status === "Présent") present += 1;
        if (status === "Absent") absent += 1;
        if (status === "Malade/Blessé") sick += 1;
      });

      return {
        day,
        label: day.split("-")[2],
        present,
        absent,
        sick,
        total: present + absent + sick,
      };
    });
  }, [days, adherents, attendanceData]);

  const activeSessionDays = useMemo(() => {
    return dailyStats.filter((item) => item.total > 0);
  }, [dailyStats]);

  const adherentStats = useMemo(() => {
    return adherents.map((adherent) => {
      const statuses = days
        .map((day) => attendanceData[String(adherent.id)]?.[day])
        .filter(Boolean);

      const presentCount = statuses.filter((s) => s === "Présent").length;
      const absentCount = statuses.filter((s) => s === "Absent").length;
      const sickCount = statuses.filter((s) => s === "Malade/Blessé").length;
      const filledCount = statuses.length;
      const rate = filledCount > 0 ? Math.round((presentCount / filledCount) * 100) : 0;

      return {
        adherent,
        presentCount,
        absentCount,
        sickCount,
        filledCount,
        rate,
      };
    });
  }, [adherents, days, attendanceData]);

  const bestPresenceCount = useMemo(() => {
    const max = Math.max(0, ...adherentStats.map((item) => item.presentCount));
    return max;
  }, [adherentStats]);

  const ranking = useMemo(() => {
    return [...adherentStats].sort((a, b) => {
      if (b.presentCount !== a.presentCount) return b.presentCount - a.presentCount;
      if (b.rate !== a.rate) return b.rate - a.rate;
      return `${a.adherent.nom} ${a.adherent.prenom}`.localeCompare(
        `${b.adherent.nom} ${b.adherent.prenom}`
      );
    });
  }, [adherentStats]);

  const chartRankingData = useMemo(() => {
    return ranking.slice(0, 10).map((item) => ({
      name: getShortName(item.adherent),
      taux: item.rate,
      presences: item.presentCount,
    }));
  }, [ranking]);

  const totalPresent = useMemo(
    () => adherentStats.reduce((acc, item) => acc + item.presentCount, 0),
    [adherentStats]
  );

  const totalAbsent = useMemo(
    () => adherentStats.reduce((acc, item) => acc + item.absentCount, 0),
    [adherentStats]
  );

  const totalSick = useMemo(
    () => adherentStats.reduce((acc, item) => acc + item.sickCount, 0),
    [adherentStats]
  );

  const totalFilled = useMemo(
    () => adherentStats.reduce((acc, item) => acc + item.filledCount, 0),
    [adherentStats]
  );

  const globalRate = totalFilled > 0 ? Math.round((totalPresent / totalFilled) * 100) : 0;

  const accessibleDojosCount = useMemo(() => {
    if (!isTeacher) return dojos.length;
    return Object.values(teacherAccessMap).filter(
      (groupsList) => Array.isArray(groupsList) && groupsList.length > 0
    ).length;
  }, [isTeacher, dojos.length, teacherAccessMap]);

  const handleDojoSelect = async (dojoId) => {
    const dojoKey = String(dojoId);

    setSelectedDojo(dojoKey);
    setSelectedGroup("");
    setAdherents([]);
    setAttendanceData({});

    if (!isTeacher) {
      const existing = allGroups.filter(
        (group) => getGroupDojoId(group) === dojoKey
      );

      if (existing.length === 0) {
        const loadedGroups = await loadGroupsByDojo(dojoKey);
        setAllGroups((prev) => {
          const ids = new Set(prev.map((item) => item.id));
          const merged = [...prev];
          loadedGroups.forEach((group) => {
            if (!ids.has(group.id)) merged.push(group);
          });
          return merged;
        });
      }
    }
  };

  const handleDeleteMonthlyAttendance = async () => {
    if (!selectedGroup) return;

    const label = formatMonthLabel(selectedMonth);

    if (demoMode) {
      addNotification("Fonction indisponible en mode Démo.", "warning");
      return;
    }

    const step1 = window.confirm(
      `Cette action va supprimer toutes les présences du groupe sélectionné pour ${label}. Continuer ?`
    );
    if (!step1) return;

    const step2 = window.prompt(
      `Tape "${selectedMonth}" pour confirmer la suppression de ${label} :`
    );

    if (step2 !== selectedMonth) {
      addNotification("Confirmation invalide. Suppression annulée.", "warning");
      return;
    }

    try {
      setDeletingMonth(true);
      await attendanceServices.deleteMonthlyAttendance(selectedGroup, selectedMonth);
      await fetchGroupMonthlyData(selectedGroup, selectedMonth);
      addNotification(`Présences de ${label} supprimées avec succès.`, "success");
    } catch (error) {
      console.error("Erreur pendant la suppression mensuelle :", error);
      addNotification("Erreur lors de la suppression mensuelle.", "error");
    } finally {
      setDeletingMonth(false);
    }
  };

  return (
    <div className="w-full bg-slate-50 pb-12 pt-[100px] dark:bg-slate-950">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        {/* Header */}
        <section className="mt-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Suivi de présence
              </p>
              <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">
                Tableau mensuel
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-400">
                Visualisez les présences du mois, repérez les meilleurs taux et
                comparez rapidement vos adhérents groupe par groupe.
              </p>
            </div>

            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              Accueil
            </button>
          </div>
        </section>

        {/* Club + KPI */}
        <section className="mt-5">
          {loading && !club ? (
            <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
              <div className="animate-pulse overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="h-24 bg-slate-200 dark:bg-slate-800" />
                <div className="flex items-center gap-3 p-4">
                  <div className="h-14 w-14 rounded-full bg-slate-200 dark:bg-slate-800" />
                  <div className="flex-1">
                    <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-800" />
                    <div className="mt-2 h-3 w-40 rounded bg-slate-200 dark:bg-slate-800" />
                  </div>
                </div>
              </div>

              <div className="animate-pulse rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>
            </div>
          ) : club ? (
            <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-900 px-5 py-4 text-white">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
                    Club
                  </p>
                  <h2 className="mt-2 text-xl font-black leading-tight">
                    {club.nom_club}
                  </h2>
                  <p className="mt-1 text-sm text-white/80">
                    {club.departement_club || "Département non renseigné"}
                  </p>
                </div>

                <div className="flex items-center gap-3 p-4">
                  {club.logo_url ? (
                    <img
                      src={club.logo_url}
                      alt={club.nom_club}
                      className="h-16 w-16 rounded-full border-2 border-white object-cover shadow-lg ring-1 ring-slate-200 dark:border-slate-900 dark:ring-slate-700"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500 shadow-lg dark:bg-slate-800 dark:text-slate-300">
                      <FaUsers className="text-2xl" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-3 text-sm leading-5 text-slate-600 dark:text-slate-400">
                      {club.adresse_club || "Adresse non renseignée"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <KpiCard
                    icon={<FaMapMarkerAlt />}
                    label="Lieux"
                    value={accessibleDojosCount}
                    colorClass="bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                  />

                  <KpiCard
                    icon={<FaLayerGroup />}
                    label="Groupes"
                    value={selectedDojo ? availableGroups.length : allGroups.length}
                    colorClass="bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300"
                  />

                  <KpiCard
                    icon={<FaUsers />}
                    label="Adhérents"
                    value={adherents.length}
                    colorClass="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                  />

                  <KpiCard
                    icon={<FaCalendarAlt />}
                    label="Mois"
                    value={formatMonthLabel(selectedMonth)}
                    colorClass="bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                  />
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-800/60">
                  {isTeacher ? (
                    <div className="flex flex-wrap items-center gap-2 text-slate-700 dark:text-slate-300">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 font-semibold text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                        <FaUserCheck />
                        Accès enseignant
                      </span>
                      <span>
                        {teacher?.prenom} {teacher?.nom} · {accessibleDojosCount} lieu(x)
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2 text-slate-700 dark:text-slate-300">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 font-semibold text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                        <FaUserCheck />
                        Accès complet
                      </span>
                      <span>
                        Vous pouvez consulter tous les lieux et groupes du club.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </section>

        {/* Toolbar */}
        <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Mois
                </label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                />
              </div>

              <div className="pt-0 xl:pt-7">
                <button
                  type="button"
                  onClick={handleDeleteMonthlyAttendance}
                  disabled={!selectedGroup || demoMode || deletingMonth}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-2xl px-4 py-3 font-semibold text-white transition",
                    !selectedGroup || demoMode || deletingMonth
                      ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                      : "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                  )}
                  title={
                    !selectedGroup
                      ? "Sélectionne un groupe d'abord"
                      : demoMode
                      ? "Indisponible en mode Démo"
                      : "Supprimer les présences du mois pour ce groupe"
                  }
                >
                  <FaTrash />
                  {deletingMonth ? "Suppression..." : "Supprimer le mois"}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-300">
              {selectedGroupObject ? (
                <>
                  Groupe sélectionné :{" "}
                  <span className="font-bold text-slate-900 dark:text-slate-100">
                    {selectedGroupObject.nom_groupe}
                  </span>
                </>
              ) : (
                "Sélectionne un lieu puis un groupe pour afficher le tableau mensuel."
              )}
            </div>
          </div>
        </section>

        {/* Dojos */}
        <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-4 flex items-center gap-2">
            <FaMapMarkerAlt className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
              Lieux
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {dojos.map((dojo) => (
              <DojoCard
                key={dojo.id}
                dojo={dojo}
                active={String(selectedDojo) === String(dojo.id)}
                onClick={() => handleDojoSelect(dojo.id)}
              />
            ))}
          </div>
        </section>

        {/* Groupes */}
        {selectedDojo && (
          <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-4 flex items-center gap-2">
              <FaLayerGroup className="text-purple-600 dark:text-purple-400" />
              <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                Groupes
              </h2>
            </div>

            {availableGroups.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {availableGroups.map((group) => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    active={String(selectedGroup) === String(group.id)}
                    onClick={() => setSelectedGroup(String(group.id))}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-400">
                Aucun groupe disponible pour ce lieu.
              </div>
            )}
          </section>
        )}

        {/* Stats du mois */}
        {selectedGroup && (
          <section className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                icon={<FaCheckCircle />}
                label="Présences"
                value={totalPresent}
                colorClass="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
              />

              <KpiCard
                icon={<FaTimesCircle />}
                label="Absences"
                value={totalAbsent}
                colorClass="bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300"
              />

              <KpiCard
                icon={<FaMedkit />}
                label="Malades"
                value={totalSick}
                colorClass="bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
              />

              <KpiCard
                icon={<FaPercentage />}
                label="Taux global"
                value={`${globalRate}%`}
                colorClass="bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
              />
            </div>
          </section>
        )}

        {/* Classement */}
        {/* {selectedGroup && ranking.length > 0 && (
          <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-4 flex items-center gap-2">
              <FaTrophy className="text-amber-500" />
              <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                Classement du mois
              </h2>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {ranking.slice(0, 8).map((item, index) => {
                const isBest =
                  bestPresenceCount > 0 && item.presentCount === bestPresenceCount;

                return (
                  <div
                    key={item.adherent.id}
                    className={cn(
                      "rounded-2xl border p-4",
                      isBest
                        ? "border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10"
                        : "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-black text-slate-900 dark:text-slate-100">
                          #{index + 1} {item.adherent.prenom} {item.adherent.nom}
                        </p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {item.presentCount} présence(s) · {item.rate}%
                        </p>
                      </div>

                      {isBest && (
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-500 text-white">
                          <FaTrophy />
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )} */}

        {/* Graphiques */}
        {selectedGroup && (
          <section className="mt-6 grid gap-6 xl:grid-cols-1">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-4 flex items-center gap-2">
                <FaChartBar className="text-blue-600 dark:text-blue-400" />
                <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                  Évolution quotidienne
                </h2>
              </div>

              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="present" name="Présents" stackId="a" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="absent" name="Absents" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="sick" name="Malades/Blessés" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-4 flex items-center gap-2">
                <FaChartBar className="text-purple-600 dark:text-purple-400" />
                <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                  Taux de présence
                </h2>
              </div>

              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartRankingData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" interval={0} angle={-18} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} domain={[0, 100]} />
                    <Tooltip formatter={(value, name) => [`${value}${name === "taux" ? "%" : ""}`, name === "taux" ? "Taux" : "Présences"]} />
                    <Legend />
                    <Bar dataKey="taux" name="Taux (%)" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div> */}
          </section>
        )}

        {/* Tableau */}
        {selectedGroup && (
          <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-4 flex items-center gap-2">
              <FaTable className="text-slate-700 dark:text-slate-200" />
              <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                Tableau mensuel
              </h2>
            </div>

            <div className="overflow-auto rounded-2xl border border-slate-200 dark:border-slate-800">
              <table className="min-w-[1100px] border-collapse text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-100 dark:bg-slate-800">
                    <th className="sticky left-0 z-20 min-w-[240px] border-b border-r border-slate-200 bg-slate-100 px-4 py-3 text-left font-black text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                      Adhérent
                    </th>

                    <th className="border-b border-r border-slate-200 px-3 py-3 text-center font-black text-slate-700 dark:border-slate-700 dark:text-slate-200">
                      P
                    </th>

                    <th className="border-b border-r border-slate-200 px-3 py-3 text-center font-black text-slate-700 dark:border-slate-700 dark:text-slate-200">
                      %
                    </th>

                    {days.map((day) => (
                      <th
                        key={day}
                        className="border-b border-r border-slate-200 px-3 py-3 text-center font-black text-slate-700 dark:border-slate-700 dark:text-slate-200"
                      >
                        {day.split("-")[2]}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {ranking.length > 0 ? (
                    ranking.map((item) => {
                      const isBest =
                        bestPresenceCount > 0 &&
                        item.presentCount === bestPresenceCount;

                      return (
                        <tr
                          key={item.adherent.id}
                          className="border-t border-slate-200 dark:border-slate-800"
                        >
                          <td className="sticky left-0 z-10 border-r border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-sm font-black text-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                {getInitials(item.adherent)}
                              </div>

                              <div className="min-w-0">
                                <p className="font-bold text-slate-900 dark:text-slate-100">
                                  {item.adherent.prenom} {item.adherent.nom}
                                </p>
                                <div className="mt-1 flex items-center gap-2">
                                  {isBest && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                                      <FaTrophy />
                                      Meilleur score
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="border-r border-slate-200 px-3 py-3 text-center font-black text-slate-900 dark:border-slate-700 dark:text-slate-100">
                            {item.presentCount}
                          </td>

                          <td className="border-r border-slate-200 px-3 py-3 text-center font-black text-slate-900 dark:border-slate-700 dark:text-slate-100">
                            {item.rate}%
                          </td>

                          {days.map((day) => {
                            const status =
                              attendanceData[String(item.adherent.id)]?.[day] || "";

                            return (
                              <td
                                key={day}
                                className="border-r border-slate-200 px-1.5 py-2 text-center dark:border-slate-700"
                              >
                                <span
                                  className={cn(
                                    "inline-flex h-8 w-8 items-center justify-center rounded-lg text-xs font-black",
                                    getStatusCellClass(status)
                                  )}
                                  title={status || "Aucune donnée"}
                                >
                                  {getStatusShortLabel(status)}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={days.length + 3}
                        className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                      >
                        Aucun adhérent ou aucune donnée de présence pour ce mois.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {activeSessionDays.length > 0 && (
              <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                {activeSessionDays.length} séance(s) enregistrée(s) sur{" "}
                {formatMonthLabel(selectedMonth)}.
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default AffichagePresence;